import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { fetchFtpConfig, saveFtpConfig } from "../lib/api";
import { Save, Check, Server } from "lucide-react";

const TIERS = ["A", "B", "C", "D", "E", "F", "G", "H"];

export default function GlobalParameters({ vatRate, setVatRate, minQty, setMinQty, fallbackTier, setFallbackTier }) {
  const [ftpHost, setFtpHost] = useState("");
  const [ftpUsername, setFtpUsername] = useState("");
  const [ftpPassword, setFtpPassword] = useState("");
  const [hasSavedPassword, setHasSavedPassword] = useState(false);
  const [ftpDirty, setFtpDirty] = useState(false);
  const [ftpSaving, setFtpSaving] = useState(false);
  const [ftpSavedFlash, setFtpSavedFlash] = useState(false);

  useEffect(() => {
    fetchFtpConfig()
      .then((cfg) => {
        setFtpHost(cfg.host || "");
        setFtpUsername(cfg.username || "");
        setHasSavedPassword(!!cfg.has_password);
      })
      .catch(() => toast.error("Failed to load FTP settings"));
  }, []);

  const handleFtpSave = async () => {
    if (!ftpHost || !ftpUsername || (!ftpPassword && !hasSavedPassword)) {
      toast.error("FTP Server IP, Username, and Password are required");
      return;
    }
    setFtpSaving(true);
    try {
      const saved = await saveFtpConfig({ host: ftpHost, username: ftpUsername, password: ftpPassword || undefined });
      setHasSavedPassword(saved.has_password);
      setFtpPassword("");
      setFtpDirty(false);
      setFtpSavedFlash(true);
      setTimeout(() => setFtpSavedFlash(false), 2500);
      toast.success("FTP settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setFtpSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <section className="border border-[#E2E8F0] bg-white sharp">
        <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] px-6 py-3">
          <div className="eyebrow text-[#64748B]">Global Parameters</div>
          <div className="text-xs text-[#64748B] mt-1">Shared settings applied whenever pricing is computed.</div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label className="eyebrow text-[#64748B]">VAT Rate (%)</Label>
            <Input
              type="number" min={0} max={100} step={0.1}
              value={vatRate}
              onChange={(e) => setVatRate(parseFloat(e.target.value) || 0)}
              className="mt-2 rounded-sm font-mono-data border-[#E2E8F0] focus-visible:ring-[#F5C800]"
              data-testid="vat-rate-input"
            />
          </div>
          <div>
            <Label className="eyebrow text-[#64748B]">Min Quantity Threshold</Label>
            <Input
              type="number" min={0}
              value={minQty}
              onChange={(e) => setMinQty(parseFloat(e.target.value) || 0)}
              className="mt-2 rounded-sm font-mono-data border-[#E2E8F0] focus-visible:ring-[#F5C800]"
              data-testid="min-qty-input"
            />
          </div>
          <div>
            <Label className="eyebrow text-[#64748B]">Default Fallback Tier</Label>
            <Select value={fallbackTier} onValueChange={setFallbackTier}>
              <SelectTrigger className="mt-2 rounded-sm border-[#E2E8F0]" data-testid="fallback-tier-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => (
                  <SelectItem key={t} value={t}>Tier {t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      <section className="border border-[#E2E8F0] bg-white sharp">
        <div className="border-b border-[#E2E8F0] bg-[#F8F9FA] px-6 py-3 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="eyebrow text-[#64748B] flex items-center gap-2">
              <Server size={13} /> FTP Server Connection
            </div>
            <div className="text-xs text-[#64748B] mt-1">Used by "Load Data" to pull the latest pricing file.</div>
          </div>
          <div className="flex items-center gap-2">
            {ftpSavedFlash && (
              <span className="text-[12px] text-[#16A34A] flex items-center gap-1 slide-in font-semibold">
                <Check size={14} /> Saved
              </span>
            )}
            <Button
              size="sm"
              onClick={handleFtpSave}
              disabled={!ftpDirty || ftpSaving}
              className="rounded-sm h-8 text-xs gap-2 bg-[#1A2340] hover:bg-[#253060] text-white disabled:opacity-40"
              data-testid="save-ftp-config-button"
            >
              <Save size={13} /> {ftpSaving ? "Saving…" : "Save FTP Settings"}
            </Button>
          </div>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <Label className="eyebrow text-[#64748B]">FTP Server IP</Label>
            <Input
              type="text"
              placeholder="e.g. 192.168.1.10"
              value={ftpHost}
              onChange={(e) => { setFtpHost(e.target.value); setFtpDirty(true); }}
              className="mt-2 rounded-sm font-mono-data border-[#E2E8F0] focus-visible:ring-[#F5C800]"
              data-testid="ftp-host-input"
            />
          </div>
          <div>
            <Label className="eyebrow text-[#64748B]">Username</Label>
            <Input
              type="text"
              autoComplete="off"
              value={ftpUsername}
              onChange={(e) => { setFtpUsername(e.target.value); setFtpDirty(true); }}
              className="mt-2 rounded-sm font-mono-data border-[#E2E8F0] focus-visible:ring-[#F5C800]"
              data-testid="ftp-username-input"
            />
          </div>
          <div>
            <Label className="eyebrow text-[#64748B]">Password</Label>
            <Input
              type="password"
              autoComplete="new-password"
              placeholder={hasSavedPassword ? "•••••••• (saved)" : ""}
              value={ftpPassword}
              onChange={(e) => { setFtpPassword(e.target.value); setFtpDirty(true); }}
              className="mt-2 rounded-sm font-mono-data border-[#E2E8F0] focus-visible:ring-[#F5C800]"
              data-testid="ftp-password-input"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
